var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { Children, isValidElement } from 'react';
import { usePermissions, useResourceContext, useRecordContext, Tab as RaTab, } from 'react-admin';
import { canAccess } from '../canAccess';
/**
 * Replacement for the show <Tab> that only renders a tab if the user has the right permissions.
 *
 * Add a name prop to the Tab to define the sub-resource on which the user needs to have the 'read' permissions for.
 *
 * <Tab> also only renders the child fields for which the user has the 'read' permissions.
 *
 * @example
 * import { Show, TabbedShowLayout, TextField } from 'react-admin';
 * import { Tab } from '@react-admin/ra-rbac';
 *
 * const authProvider = {
 *      // ...
 *      getPermissions: () => Promise.resolve([
 *          { action: ['list', 'show'], resource: 'products' },
 *          { action: 'read', resource: 'products.reference' },
 *          { action: 'read', resource: 'products.width' },
 *          { action: 'read', resource: 'products.height' },
 *          // 'products.description' is missing
 *          { action: 'read', resource: 'products.thumbnail' },
 *          // 'products.image' is missing
 *          { action: 'read', resource: 'products.tab.description' },
 *          { action: 'read', resource: 'products.tab.images' },
 *          // 'products.tab.stock' is missing
 *      ]),
 * };
 *
 * const ProductShow = () => (
 *    <Show>
 *        <TabbedShowLayout>
 *            <Tab label="Description" name="description">
 *                <TextField source="reference" />
 *                <TextField source="width" />
 *                <TextField source="height" />
 *                {// Not displayed }
 *                <TextField source="description" />
 *            </Tab>
 *            <Tab label="Images" name="images">
 *                <TextField source="image" />
 *                {// Not displayed }
 *                <TextField source="thumbnail" />
 *            </Tab>
 *            {// Not displayed }
 *            <Tab label="Stock" name="stock">
 *                <TextField source="stock" />
 *            </Tab>
 *        </TabbedShowLayout>
 *    </Show>
 * );
 */
export var Tab = function (_a) {
    var name = _a.name, children = _a.children, props = __rest(_a, ["name", "children"]);
    var resource = useResourceContext();
    var record = useRecordContext();
    var _b = usePermissions(), isLoading = _b.isLoading, permissions = _b.permissions;
    if (isLoading ||
        !canAccess({
            permissions: permissions,
            action: 'read',
            resource: "".concat(resource, ".tab.").concat(name),
            record: record,
        })) {
        return null;
    }
    return (React.createElement(RaTab, __assign({}, props), Children.map(children, function (child) {
        return isValidElement(child) &&
            canAccess({
                permissions: permissions,
                action: 'read',
                resource: "".concat(resource, ".").concat(child.props.source),
                record: record,
            })
            ? child
            : null;
    })));
};
